"use client";

import React from "react";
import * as Sentry from "@sentry/nextjs";
import { reportError, isNextInternalThrow } from "@/lib/report-error";

export type BoundaryName =
  | "root"
  | "app-page"
  | "auth"
  | "admin-activity"
  | "wedding-checkin"
  | "weddings-portal"
  | "share-public-fabrics";

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  name?: BoundaryName;
  // Override the wrapper class to keep flex/grid layouts working when
  // nesting boundaries deep in the tree.
  wrapperClassName?: string;
};

type State = { hasError: boolean };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };
  // Wrapper persists across the error→fallback swap, so the ref stays
  // valid; used to scope the screenshot to this boundary's subtree.
  private containerRef = React.createRef<HTMLDivElement>();

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo): void {
    // Next.js throws with digest NEXT_REDIRECT / NEXT_NOT_FOUND as control
    // flow from server actions; don't log them as exceptions.
    if (isNextInternalThrow(err)) return;
    // This boundary swallows the error, so without an explicit capture a React
    // render crash would reach wf_client_error_log but never Sentry.
    Sentry.captureException(err, {
      contexts: { react: { componentStack: info.componentStack } },
      tags: { boundary: this.props.name ?? "root" },
    });
    reportError(
      {
        kind: "js_exception",
        message: err.message || "Unknown error",
        stack: err.stack,
        context: {
          component_stack: info.componentStack,
          boundary: this.props.name ?? "root",
        },
      },
      this.containerRef.current,
    );
  }

  render(): React.ReactNode {
    return (
      <div ref={this.containerRef} className={this.props.wrapperClassName}>
        {this.state.hasError
          ? (this.props.fallback ?? (
              <div role="alert" className="p-4 text-sm text-red-700">
                Something went wrong. The error has been reported.
              </div>
            ))
          : this.props.children}
      </div>
    );
  }
}
